import { makeAutoObservable } from 'mobx'
import { addSeconds } from 'date-fns/addSeconds'
import { differenceInCalendarMonths } from 'date-fns/differenceInCalendarMonths'
import { endOfMonth } from 'date-fns/endOfMonth'
import { subMonths } from 'date-fns/subMonths'

import type { DateRange, DetailedCategoryWithTransactions } from '../types'
import { DATE_FORMATS_INTL, formatDate } from '../constants'
import {
  buildCategoryDetailsChartData,
  filterAndSortCategoriesWithTransactions,
} from '../utils/TrendsUtil'

import { GlobalStore } from './GlobalStore'

export class TrendsStore {
  globalStore: GlobalStore
  selectedCategoryData: DetailedCategoryWithTransactions | null = null
  selectedDateRange: DateRange
  visibleListLength: number = 5

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.selectedDateRange = {
      start: addSeconds(endOfMonth(subMonths(new Date(), 6)), 1),
      end: endOfMonth(new Date()),
    }
    makeAutoObservable(this)
  }

  setSelectedCategoryData = (data: DetailedCategoryWithTransactions | null) => {
    this.selectedCategoryData = data
  }

  setSelectedDateRange = (dateRange: DateRange) => {
    this.selectedDateRange = dateRange
  }

  setVisibleListLength = (length: number) => {
    this.visibleListLength = length
  }

  get sortedDetailedCategoriesWithTransactions(): DetailedCategoryWithTransactions[] {
    return filterAndSortCategoriesWithTransactions(
      this.globalStore.categoryStore.detailedCategoriesWithTransactions,
    )
  }

  get selectedDateRangeMonthCount(): number {
    return differenceInCalendarMonths(this.selectedDateRange.end, this.selectedDateRange.start) + 1
  }

  get selectedDateRangeMonthRange(): string {
    const startMonth = formatDate(this.selectedDateRange.start, DATE_FORMATS_INTL.MONTH_LONG)
    const endMonth = formatDate(this.selectedDateRange.end, DATE_FORMATS_INTL.MONTH_LONG)
    return `${startMonth} - ${endMonth}`
  }

  get visibleCategories(): DetailedCategoryWithTransactions[] {
    return this.sortedDetailedCategoriesWithTransactions.slice(0, this.visibleListLength)
  }

  get collapsedCategories(): DetailedCategoryWithTransactions[] {
    return this.sortedDetailedCategoriesWithTransactions.slice(this.visibleListLength)
  }

  get categoryDetailsChartData() {
    return buildCategoryDetailsChartData(
      this.selectedCategoryData,
      this.selectedDateRangeMonthCount,
    )
  }
}
